import { size } from 'lodash-es';
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';

import { Divider, MenuItem, PaperProps, Popover } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';

import * as Api from 'contracts/api';
import * as Models from 'contracts/models';
import { useSetDefaultLicense } from 'queries/user-action/use-set-default-license';

import useStyles from './styles';

import BarChartIcon from '../../assets/barchart.svg';
import ChatIcon from '../../assets/chat.svg';
import useZeWidget from '../../hooks/use-ze-widget';
import LicenseListItem from '../LicenseMenuItem';
import UserEmail from '../UserEmail';

interface Props {
    user: Models.UserInfo.User;
    license: Models.UserInfo.License;
    licenseList: Models.UserInfo.License[];
    isOpen: boolean;
    getPaperProps?: () => PaperProps;
    onClose?: () => void;
    anchor: () => Element;
    container?: () => Element;
    className?: string;
}

const UserMenu: React.FC<Props> = ({
    user,
    license,
    licenseList,
    isOpen,
    getPaperProps,
    onClose,
    anchor,
    container,
    className
}) => {
    const { classes, cx } = useStyles();

    const setDefaultLicenseMutation = useSetDefaultLicense();
    const isLoggingOut = useIsMutating({ mutationKey: Api.V3.UserAction.UserLogout.MutationKey() }) > 0;

    const [zeVisible, openZe] = useZeWidget();

    const onSelectLicenseClick = useCallback(
        ({ id }: Models.UserInfo.License) => {
            if (id !== license.id) {
                setDefaultLicenseMutation.mutate({ licenseId: id }, { onSuccess: () => onClose?.() });
            }
        },
        [license.id, onClose, setDefaultLicenseMutation]
    );

    const onSupportClick = useCallback(() => {
        openZe();
        onClose?.();
    }, [onClose, openZe]);

    const isMutating = setDefaultLicenseMutation.isPending || isLoggingOut;

    return (
        <Popover
            open={isOpen}
            anchorEl={anchor}
            container={container}
            onClose={onClose}
            className={cx(classes.popover, className)}
            disablePortal
            slotProps={getPaperProps ? { paper: getPaperProps() } : undefined}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'center'
            }}
            transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
            }}
        >
            <UserEmail user={user} />
            {licenseList.map((item, _, items) => {
                return (
                    <React.Fragment key={item.id}>
                        <Divider className={classes.divider} />
                        <LicenseListItem
                            license={item}
                            active={license.id === item.id}
                            readOnly={size(items) === 1}
                            disabled={!item.isValid || isMutating}
                            onSelectClick={onSelectLicenseClick}
                            onIssueClick={onClose}
                        />
                    </React.Fragment>
                );
            })}
            <Divider className={classes.divider} />
            <MenuItem
                component={'button'}
                disableGutters
                centerRipple
                disabled={isMutating || zeVisible}
                className={classes.action}
                onClick={onSupportClick}
            >
                {'Support'}
                <ChatIcon />
            </MenuItem>
            <Divider className={classes.divider} />
            <MenuItem
                component={Link}
                to={'/userinfo?tab=license'}
                onClick={onClose}
                disableGutters
                centerRipple
                disabled={isMutating}
                className={classes.action}
            >
                {'License usage statistics'}
                <BarChartIcon />
            </MenuItem>
        </Popover>
    );
};

export default UserMenu;
